import React, { useState } from 'react';
import { useApp } from '../context/AppContext';
import { useForm } from 'react-hook-form';
import { Upload, X, Save, Building, User, CreditCard, FileSignature } from 'lucide-react';
import toast from 'react-hot-toast';

const Settings = () => {
  const { state, dispatch } = useApp();
  const { settings } = state;
  const [activeTab, setActiveTab] = useState('business');
  const [paymentBarcode, setPaymentBarcode] = useState(settings.paymentBarcode);
  const [signature, setSignature] = useState(settings.signature);

  const { register, handleSubmit, formState: { errors } } = useForm({
    defaultValues: settings
  });

  const handleFileUpload = (file, type) => {
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        if (type === 'barcode') {
          setPaymentBarcode(e.target.result);
        } else if (type === 'signature') {
          setSignature(e.target.result);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const onSubmit = (data) => {
    const updatedSettings = {
      ...data,
      paymentBarcode,
      signature
    };

    dispatch({ type: 'UPDATE_SETTINGS', payload: updatedSettings });
    toast.success('Settings updated successfully!');
  };

  const tabs = [
    { id: 'business', label: 'Business Info', icon: Building },
    { id: 'account', label: 'Account', icon: User },
    { id: 'payment', label: 'Payment', icon: CreditCard }
  ];

  return (
    <div className="p-8">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold gradient-text mb-2">Settings</h1>
          <p className="text-gray-600 text-lg">Manage your business and account settings</p>
        </div>

        <div className="card">
        {/* Tab Navigation */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="-mb-px flex space-x-8">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center ${
                  activeTab === tab.id
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <tab.icon className="w-4 h-4 mr-2" />
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Business Info Tab */}
          {activeTab === 'business' && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold text-gray-900 flex items-center">
                <Building className="w-5 h-5 mr-2" />
                Business Information
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Shop Name *
                  </label>
                  <input
                    type="text"
                    {...register('shopName', { required: 'Shop name is required' })}
                    className="input-field"
                    placeholder="Enter shop name"
                  />
                  {errors.shopName && (
                    <p className="text-red-500 text-sm mt-1">{errors.shopName.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    {...register('email', { required: 'Email is required' })}
                    className="input-field"
                    placeholder="Enter email address"
                  />
                  {errors.email && (
                    <p className="text-red-500 text-sm mt-1">{errors.email.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Mobile Number *
                  </label>
                  <input
                    type="tel"
                    {...register('mobile', { required: 'Mobile number is required' })}
                    className="input-field"
                    placeholder="Enter mobile number"
                  />
                  {errors.mobile && (
                    <p className="text-red-500 text-sm mt-1">{errors.mobile.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Website
                  </label>
                  <input
                    type="url"
                    {...register('website')}
                    className="input-field"
                    placeholder="Enter website URL"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    GST Number
                  </label>
                  <input
                    type="text"
                    {...register('gstNumber')}
                    className="input-field"
                    placeholder="Enter GST number"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Business Address
                </label>
                <textarea
                  {...register('address')}
                  rows={3}
                  className="input-field"
                  placeholder="Enter complete business address"
                />
              </div>
            </div>
          )}

          {/* Account Tab */}
          {activeTab === 'account' && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold text-gray-900 flex items-center">
                <User className="w-5 h-5 mr-2" />
                Account Settings
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Username *
                  </label>
                  <input
                    type="text"
                    {...register('username', { required: 'Username is required' })}
                    className="input-field"
                    placeholder="Enter username"
                  />
                  {errors.username && (
                    <p className="text-red-500 text-sm mt-1">{errors.username.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Password *
                  </label>
                  <input
                    type="password"
                    {...register('password', { required: 'Password is required' })}
                    className="input-field"
                    placeholder="Enter password"
                  />
                  {errors.password && (
                    <p className="text-red-500 text-sm mt-1">{errors.password.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Account Number
                  </label>
                  <input
                    type="text"
                    {...register('accountNo')}
                    className="input-field"
                    placeholder="Enter account number"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    IFSC Code
                  </label>
                  <input
                    type="text"
                    {...register('ifscCode')}
                    className="input-field"
                    placeholder="Enter IFSC code"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Payment Tab */}
          {activeTab === 'payment' && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold text-gray-900 flex items-center">
                <CreditCard className="w-5 h-5 mr-2" />
                Payment Settings
              </h2>

              {/* Payment Barcode */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Payment QR Code
                </label>
                <div className="flex items-center space-x-4">
                  {paymentBarcode ? (
                    <div className="relative">
                      <img
                        src={paymentBarcode}
                        alt="Payment QR Code"
                        className="w-24 h-24 object-contain border rounded-lg"
                      />
                      <button
                        type="button"
                        onClick={() => setPaymentBarcode(null)}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1"
                      >
                        <X size={12} />
                      </button>
                    </div>
                  ) : (
                    <div className="w-24 h-24 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center">
                      <Upload className="w-8 h-8 text-gray-400" />
                    </div>
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleFileUpload(e.target.files[0], 'barcode')}
                    className="hidden"
                    id="barcode-upload"
                  />
                  <label
                    htmlFor="barcode-upload"
                    className="btn-secondary cursor-pointer"
                  >
                    Upload QR Code
                  </label>
                </div>
                <p className="text-sm text-gray-500 mt-2">
                  Upload your payment QR code for customers to scan and pay
                </p>
              </div>

              {/* Signature */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Digital Signature
                </label>
                <div className="flex items-center space-x-4">
                  {signature ? (
                    <div className="relative">
                      <img
                        src={signature}
                        alt="Signature"
                        className="w-32 h-16 object-contain border rounded-lg"
                      />
                      <button
                        type="button"
                        onClick={() => setSignature(null)}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1"
                      >
                        <X size={12} />
                      </button>
                    </div>
                  ) : (
                    <div className="w-32 h-16 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center">
                      <FileSignature className="w-8 h-8 text-gray-400" />
                    </div>
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleFileUpload(e.target.files[0], 'signature')}
                    className="hidden"
                    id="signature-upload"
                  />
                  <label
                    htmlFor="signature-upload"
                    className="btn-secondary cursor-pointer"
                  >
                    Upload Signature
                  </label>
                </div>
                <p className="text-sm text-gray-500 mt-2">
                  Upload your signature to appear on invoices
                </p>
              </div>
            </div>
          )}

          <div className="flex justify-end pt-6 border-t">
            <button
              type="submit"
              className="btn-primary"
            >
              <Save className="w-4 h-4 mr-2" />
              Save Settings
            </button>
          </div>
        </form>
        </div>
      </div>
    </div>
  );
};

export default Settings;
